import React, { useState, useEffect, useRef } from 'react';
import { useNotification } from '../../../context/NotificationContext';
import { adminAPI } from '../../../utils/api';
import { formatImageUrl } from '../../../utils/imageUtils';

const Sliders = () => {

  const { showSuccess, showError } = useNotification();
  const fileInputRef = useRef(null);
  const [sliders, setSliders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSlider, setEditingSlider] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [cancelSource, setCancelSource] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [preview, setPreview] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    link_url: '',
    order: 0,
    is_active: true
  });

  useEffect(() => {
    fetchSliders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSliders = async () => {
    try {
      const response = await adminAPI.getAllSliders();
      if (response.data.success) {
        setSliders(response.data.data || []);
      }
    } catch (error) {
      showError('Failed to load sliders');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      setFileError('File too large. Max 20MB.');
      setSelectedFile(null);
      setPreview('');
      return;
    }
    setFileError('');
    setSelectedFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const clearFile = () => {
    setSelectedFile(null);
    setPreview('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const cancelUpload = () => {
    if (cancelSource) {
      cancelSource.abort();
      setCancelSource(null);
    }
    setUploading(false);
    setUploadProgress(0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingSlider && !selectedFile) {
      showError('Please select an image');
      return;
    }
    const controller = new AbortController();
    setCancelSource(controller);
    setUploading(true);
    setUploadProgress(0);
    try {
      const data = new FormData();
      if (formData.title) data.append('title', formData.title);
      if (formData.description) data.append('description', formData.description);
      if (formData.link_url) data.append('link_url', formData.link_url);
      data.append('order', formData.order);
      data.append('is_active', formData.is_active);
      if (selectedFile) {
        data.append('image', selectedFile);
      }

      const onProgress = (e) => {
        if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
      };

      if (editingSlider) {
        await adminAPI.updateSlider(editingSlider._id, data, onProgress, controller.signal);
        showSuccess('Slider updated successfully');
      } else {
        await adminAPI.createSlider(data, onProgress, controller.signal);
        showSuccess('Slider created successfully');
      }
      setShowForm(false);
      setEditingSlider(null);
      setFormData({ title: '', description: '', link_url: '', order: 0, is_active: true });
      setSelectedFile(null);
      setPreview('');
      setUploadProgress(0);
      fetchSliders();
    } catch (error) {
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        showError('Upload cancelled');
        return;
      }
      setUploadProgress(0);
      showError(error.response?.data?.message || 'Failed to save slider');
    } finally {
      setUploading(false);
      setCancelSource(null);
    }
  };

  const handleEdit = (slider) => {
    setEditingSlider(slider);
    setFormData({
      title: slider.title,
      description: slider.description || '',
      link_url: slider.link_url || '',
      order: slider.order || 0,
      is_active: slider.is_active
    });
    setPreview(slider.image_url ? formatImageUrl(slider.image_url) : '');
    setSelectedFile(null);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this slider?')) return;
    try {
      await adminAPI.deleteSlider(id);
      showSuccess('Slider deleted successfully');
      fetchSliders();
    } catch (error) {
      showError('Failed to delete slider');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading sliders...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gold">Manage Sliders</h1>
        <button
          onClick={() => {
            setShowForm(!showForm);
            setEditingSlider(null);
            setFormData({ title: '', description: '', link_url: '', order: 0, is_active: true });
            setSelectedFile(null);
            setPreview('');
          }}
          className="bg-gold text-dark px-4 py-2 rounded-lg font-medium"
        >
          {showForm ? 'Cancel' : 'Add New Slider'}
        </button>
      </div>

      {showForm && (
        <div className="bg-gray-800 rounded-xl p-6">
          <h2 className="text-xl font-semibold mb-4 text-gold">
            {editingSlider ? 'Edit Slider' : 'Create New Slider'}
          </h2>
          <form onSubmit={handleSubmit} encType="multipart/form-data" className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Title</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="w-full bg-gray-700 text-light rounded-lg px-4 py-2 border border-gray-600"
                placeholder="Optional title"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="w-full bg-gray-700 text-light rounded-lg px-4 py-2 border border-gray-600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Upload Image</label>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleFileSelect}
                disabled={uploading}
                className="w-full bg-gray-700 text-light rounded-lg px-4 py-2 border border-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-gold file:text-dark file:font-medium disabled:opacity-50"
              />
              {fileError && (
                <p className="text-red-400 text-sm mt-1">{fileError}</p>
              )}
              {preview && !uploading && (
                <div className="mt-2 relative inline-block">
                  <img src={preview} alt="Preview" className="h-32 rounded-lg object-contain" />
                  <button type="button" onClick={clearFile} disabled={uploading} className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">&times;</button>
                </div>
              )}
              {uploading && (
                <div className="mt-3 space-y-2">
                  <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
                    <div className="bg-gold h-full rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                  </div>
                  <div className="flex justify-between text-xs text-gray-400">
                    <span>{uploadProgress}% uploaded</span>
                    <button type="button" onClick={cancelUpload} className="text-red-400 hover:text-red-300 font-medium">Cancel</button>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Link URL (optional)</label>
              <input
                type="url"
                name="link_url"
                value={formData.link_url}
                onChange={handleInputChange}
                className="w-full bg-gray-700 text-light rounded-lg px-4 py-2 border border-gray-600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Order</label>
              <input
                type="number"
                name="order"
                value={formData.order}
                onChange={handleInputChange}
                className="w-full bg-gray-700 text-light rounded-lg px-4 py-2 border border-gray-600"
              />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleInputChange}
                className="mr-2"
              />
              <label className="text-sm">Active</label>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={uploading}
                className="bg-gold text-dark px-4 py-2 rounded-lg font-medium disabled:opacity-50"
              >
                {uploading ? `Uploading ${uploadProgress}%` : (editingSlider ? 'Update' : 'Create')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingSlider(null);
                  setFormData({ title: '', description: '', link_url: '', order: 0, is_active: true });
                  setSelectedFile(null);
                  setPreview('');
                }}
                className="bg-gray-700 text-light px-4 py-2 rounded-lg font-medium"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-4">
        {sliders.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <p>No sliders found. Create your first slider above.</p>
          </div>
        ) : (
          sliders.map((slider) => (
            <div key={slider._id} className="bg-gray-800 rounded-xl p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-semibold">{slider.title}</h3>
                    <span className={`px-2 py-1 rounded text-xs ${slider.is_active ? 'bg-green-600' : 'bg-gray-600'}`}>
                      {slider.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <span className="text-gray-400 text-sm">Order: {slider.order}</span>
                  </div>
                  {slider.description && (
                    <p className="text-gray-400 mb-2">{slider.description}</p>
                  )}
                  <div className="space-y-1 text-sm text-gray-400">
                    <p>Image: <span className="text-gold break-all">{slider.image_url}</span></p>
                    {slider.link_url && (
                      <p>Link: <span className="text-gold break-all">{slider.link_url}</span></p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => handleEdit(slider)}
                    className="bg-blue-600 text-white px-3 py-1 rounded text-sm"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(slider._id)}
                    className="bg-red-600 text-white px-3 py-1 rounded text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Sliders;
